import { useState } from 'react';
import Layout from 'components/Layout/Layout';
import './KostenrechnerSamenspende.scss';

const ADDONS = [
  { label: 'EmbryoScope 3 Tage', cost: 350 },
  { label: 'EmbryoScope 5 Tage (inkl. Blasto)', cost: 790 },
  { label: 'PICSI', cost: 200 },
  { label: 'ZyMot', cost: 180 },
  { label: 'EmbryoGlue', cost: 190 },
  { label: 'Assisted Hatching', cost: 160 },
  { label: 'Kryo überzähliger Embryonen', cost: 760 },
];

const fmt = (n: number) => n.toLocaleString('de-DE') + ' EUR';
const fmtR = (lo: number, hi: number) =>
  lo === hi ? fmt(lo) : lo.toLocaleString('de-DE') + ' – ' + hi.toLocaleString('de-DE') + ' EUR';

const KostenrechnerSamenspende = () => {
  const [mot5, setMot5] = useState(500);
  const [mot10, setMot10] = useState(900);
  const [mot20, setMot20] = useState(1000);
  const [mot20plus, setMot20plus] = useState(1500);
  const [checkedAddons, setCheckedAddons] = useState<boolean[]>(ADDONS.map(() => false));

  const toggleAddon = (i: number) => {
    setCheckedAddons((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };

  const addons = ADDONS.reduce((sum, a, i) => sum + (checkedAddons[i] ? a.cost : 0), 0);
  const einlagerung = 150;

  const ivfHalmLo = Math.max(0, mot5);
  const ivfHalmHi = Math.max(0, mot10);
  const iuiHalmLo = Math.max(0, mot20);
  const iuiHalmHi = Math.max(0, mot20plus);

  const iuiMedLo = 150, iuiMedHi = 300;
  const ivfMedLo = 500, ivfMedHi = 1500;

  // IUI: 3 Versuche
  const iuiN = 3;
  const iuiBehandlung = 1100;
  const iuiLabor = 100;
  const iuiTotalLo = (iuiBehandlung + iuiLabor + iuiMedLo) * iuiN + iuiHalmLo * iuiN + einlagerung;
  const iuiTotalHi = (iuiBehandlung + iuiLabor + iuiMedHi) * iuiN + iuiHalmHi * iuiN + einlagerung;
  const iuiAvg = Math.round((iuiTotalLo + iuiTotalHi) / 2);

  // IVF: 1 Zyklus
  const ivfBehandlung = 3500;
  const ivfNarkose = 400;
  const ivfLabor = 200;
  const ivfTotalLo = ivfBehandlung + ivfLabor + ivfNarkose + ivfMedLo + ivfHalmLo + addons + einlagerung;
  const ivfTotalHi = ivfBehandlung + ivfLabor + ivfNarkose + ivfMedHi + ivfHalmHi + addons + einlagerung;
  const ivfAvg = Math.round((ivfTotalLo + ivfTotalHi) / 2);

  const diff = iuiAvg - ivfAvg;

  const iuiMedAvg = Math.round((iuiMedLo + iuiMedHi) / 2) * iuiN;
  const ivfMedAvg = Math.round((ivfMedLo + ivfMedHi) / 2);
  const iuiSpar = Math.round(iuiMedAvg * 0.45);
  const ivfSpar = Math.round(ivfMedAvg * 0.45);

  return (
    <Layout>
      <div className='kostenrechner'>
        <header className='kr-header'>
          <span className='kr-clinic'>Kinderwunsch am Schlossplatz – Stuttgart</span>
          <h1>Kostenrechner Samenspende</h1>
          <span className='kr-sub'>
            Kostenvergleich: 3 IUI-Zyklen vs. 1 IVF-Zyklus mit Spendersamen – die zwei Standardwege zur Behandlung.
          </span>
        </header>

        <div className='kr-grid'>
          {/* Left column: inputs */}
          <div>
            <section className='kr-panel'>
              <h2>Kosten Spendersamen je Straw</h2>
              <div className='mot-grid'>
                {[
                  { id: 'mot5', label: 'MOT 5', value: mot5, setter: setMot5, tag: 'IVF', tagClass: 'ivf' },
                  { id: 'mot10', label: 'MOT 10', value: mot10, setter: setMot10, tag: 'IVF', tagClass: 'ivf' },
                  { id: 'mot20', label: 'MOT 20', value: mot20, setter: setMot20, tag: 'IUI', tagClass: 'iui' },
                  { id: 'mot20plus', label: 'MOT 20+', value: mot20plus, setter: setMot20plus, tag: 'IUI', tagClass: 'iui' },
                ].map(({ id, label, value, setter, tag, tagClass }) => (
                  <div key={id} className='mot-field'>
                    <span className='mot-label'>{label}</span>
                    <input
                      type='number'
                      min={0}
                      max={3000}
                      step={50}
                      value={value}
                      onChange={(e) => setter(Math.max(0, parseInt(e.target.value) || 0))}
                    />
                    <span className='unit'>EUR</span>
                    <span className={`mot-tag ${tagClass}`}>{tag}</span>
                  </div>
                ))}
              </div>
              <p className='kr-note'>
                Preise je nach Samenbank und Spender. IVF benötigt MOT 5–10, IUI benötigt MOT 20–20+. Unter MOT 20 macht IUI keinen Sinn.
              </p>
            </section>

            <section className='kr-panel'>
              <h2>Zusatzmaßnahmen IVF</h2>
              <div>
                {ADDONS.map((addon, i) => (
                  <label key={addon.label} className='addon-row' onClick={() => toggleAddon(i)}>
                    <span className='addon-left'>
                      <input
                        type='checkbox'
                        checked={checkedAddons[i]}
                        onChange={() => toggleAddon(i)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {addon.label}
                    </span>
                    <span className='addon-cost'>{addon.cost} EUR</span>
                  </label>
                ))}
              </div>
              <p className='kr-note' style={{ marginTop: 12 }}>
                Zusatzmaßnahmen sind nur bei IVF möglich, nicht bei IUI.
              </p>
            </section>
          </div>

          {/* Right column: results */}
          <div>
            <div className='kr-compare'>
              {/* IUI */}
              <div className='kr-col iui'>
                <div className='col-tag'>Weg A: 3x IUI</div>
                <div className='col-big'>~{fmt(iuiAvg)}</div>
                <div className='col-premise'>
                  3 Inseminationen – typischer erster Behandlungsschritt bei Samenspende
                </div>
                <div className='breakdown'>
                  <div className='brow'>
                    <span className='k'>3x Behandlung</span>
                    <span className='v'>{fmt(iuiBehandlung * iuiN)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>3x Blutlabor Enders</span>
                    <span className='v'>~{fmt(iuiLabor * iuiN)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>3x Spendersamen (MOT 20–20+)</span>
                    <span className='v'>{fmtR(iuiHalmLo * iuiN, iuiHalmHi * iuiN)}</span>
                  </div>
                  <div className='brow brow-sub'>
                    <span className='k'>je Straw</span>
                    <span className='v'>{fmtR(iuiHalmLo, iuiHalmHi)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>Einlagerung</span>
                    <span className='v'>
                      {fmt(einlagerung)}
                      <span className='kr-pill rose'>einmalig</span>
                    </span>
                  </div>
                  <div className='brow brow-total'>
                    <span className='k'>Behandlungskosten</span>
                    <span className='v'>
                      {fmtR(
                        (iuiBehandlung + iuiLabor) * iuiN + iuiHalmLo * iuiN + einlagerung,
                        (iuiBehandlung + iuiLabor) * iuiN + iuiHalmHi * iuiN + einlagerung,
                      )}
                    </span>
                  </div>
                  <div className='brow'>
                    <span className='k'>3x Medikamente</span>
                    <span className='v'>{fmtR(iuiMedLo * iuiN, iuiMedHi * iuiN)}</span>
                  </div>
                  <div className='brow brow-total'>
                    <span className='k'>Gesamtkosten inkl. Medikamente</span>
                    <span className='v'>{fmtR(iuiTotalLo, iuiTotalHi)}</span>
                  </div>
                </div>
              </div>

              {/* IVF */}
              <div className='kr-col ivf'>
                <div className='col-tag'>Weg B: 1x IVF</div>
                <div className='col-big'>~{fmt(ivfAvg)}</div>
                <div className='col-premise'>
                  1 IVF-Zyklus – höhere Erfolgsrate pro Versuch, Kryotransfers aus überzähligen Embryonen möglich
                </div>
                <div className='breakdown'>
                  <div className='brow'>
                    <span className='k'>Behandlung</span>
                    <span className='v'>{fmt(ivfBehandlung)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>Blutlabor Enders</span>
                    <span className='v'>~{fmt(ivfLabor)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>Narkose (medizi GmbH)</span>
                    <span className='v'>{fmt(ivfNarkose)}</span>
                  </div>
                  <div className='brow'>
                    <span className='k'>1x Spendersamen (MOT 5–10)</span>
                    <span className='v'>{fmtR(ivfHalmLo, ivfHalmHi)}</span>
                  </div>
                  {addons > 0 && (
                    <div className='brow'>
                      <span className='k'>Zusatzmaßnahmen</span>
                      <span className='v'>{fmt(addons)}</span>
                    </div>
                  )}
                  <div className='brow'>
                    <span className='k'>Einlagerung</span>
                    <span className='v'>
                      {fmt(einlagerung)}
                      <span className='kr-pill green'>einmalig</span>
                    </span>
                  </div>
                  <div className='brow brow-total'>
                    <span className='k'>Behandlungskosten</span>
                    <span className='v'>
                      {fmtR(
                        ivfBehandlung + ivfLabor + ivfNarkose + ivfHalmLo + addons + einlagerung,
                        ivfBehandlung + ivfLabor + ivfNarkose + ivfHalmHi + addons + einlagerung,
                      )}
                    </span>
                  </div>
                  <div className='brow'>
                    <span className='k'>Medikamente</span>
                    <span className='v'>{fmtR(ivfMedLo, ivfMedHi)}</span>
                  </div>
                  <div className='brow brow-total'>
                    <span className='k'>Gesamtkosten inkl. Medikamente</span>
                    <span className='v'>{fmtR(ivfTotalLo, ivfTotalHi)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Verdict */}
            <div className='kr-verdict'>
              <div className='verdict-label'>Differenz</div>
              {diff > 0 ? (
                <>
                  <div className='verdict-diff pricier'>3x IUI ist ~{fmt(diff)} teurer</div>
                  <div className='verdict-hint'>
                    Hauptgrund: 3 teure Samenhalme (MOT 20–20+) vs. 1 günstiger Halm bei IVF. IVF bietet zudem höhere kumulative Erfolgsraten.
                  </div>
                </>
              ) : diff < 0 ? (
                <>
                  <div className='verdict-diff cheaper'>3x IUI ist ~{fmt(Math.abs(diff))} günstiger</div>
                  <div className='verdict-hint'>
                    IUI ist weniger invasiv. Bei ausbleibendem Erfolg nach 3 Versuchen wird IVF empfohlen.
                  </div>
                </>
              ) : (
                <>
                  <div className='verdict-diff'>Etwa gleiche Kosten</div>
                  <div className='verdict-hint'>
                    Die Wahl hängt von medizinischen Faktoren und persönlicher Präferenz ab.
                  </div>
                </>
              )}
            </div>

            {/* Savings tip */}
            <div className='kr-savings'>
              <strong>Spartipp Medikamente:</strong> EU-Versandapotheken in Frankreich oder den Niederlanden sind bis zu 40–50 % günstiger als deutsche Apotheken. Mögliche Ersparnis: bei 3x IUI ca.{' '}
              <strong>{fmt(iuiSpar)}</strong>, bei 1x IVF ca. <strong>{fmt(ivfSpar)}</strong>. Kinderwunsch am Schlossplatz stellt das Rezept aus, die Apotheke liefert gekühlt per Express.
            </div>

            <div className='kr-disclaimer'>
              Alle Angaben sind unverbindliche Richtwerte und ersetzen keinen individuellen Kostenvoranschlag. Die tatsächlichen Kosten können je nach Behandlungsverlauf, Anzahl gewonnener Eizellen und individueller Medikation abweichen. Samenbank-Preise variieren je nach Anbieter und gewähltem Spender.
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default KostenrechnerSamenspende;
